import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  IconX,
  IconUser,
  IconClock,
  IconBuildingStore,
  IconDots,
  IconPaperclip,
  IconFile,
  IconPhoto,
  IconDownload,
  IconSend,
  IconTrash,
} from '@tabler/icons-react';
import base from '../Feedbacks/FeedbackDetail.module.scss';
import styles from './AdminFeedbackDetail.module.scss';
import {
  useGetAdminFeedbackId,
  useGetAdminFeedbackIdComments,
  usePostAdminFeedbackIdComments,
  usePutAdminFeedbackIdStatus,
  useDeleteAdminFeedbackId,
  getGetAdminFeedbackQueryKey,
  getGetAdminFeedbackIdQueryKey,
  getGetAdminFeedbackIdCommentsQueryKey,
  getGetAdminFeedbackStatsQueryKey,
} from '../../api/admin-feedback-api/admin-feedback-api';
import { FeedbackStatus, ResolutionOutcome } from '../../api/model';
import type { FeedbackTicketDto, FeedbackAttachmentDto } from '../../api/model';
import { TYPE_ICON, STATUS_COLOR, PRIORITY_COLOR, STATUS_VALUES } from '../Feedbacks/feedbackMeta';
import { formatRelative, formatDateTime, ticketRef } from '../Feedbacks/feedbackFormat';
import {
  ACCEPT_ATTR,
  MAX_FILES,
  MAX_FILE_BYTES,
  isAllowedType,
  formatFileSize,
  fileToPayload,
  downloadAttachment,
} from '../Feedbacks/feedbackAttachments';
import ConfirmDialog from '../Feedbacks/ConfirmDialog';

interface AdminFeedbackDetailProps {
  ticketId: number | null;
  fallbackTicket: FeedbackTicketDto | null;
  isOpen: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

const OUTCOME_VALUES = Object.values(ResolutionOutcome) as ResolutionOutcome[];
const OUTCOME_STATUSES: FeedbackStatus[] = [FeedbackStatus.RESOLVED, FeedbackStatus.REJECTED];
const tint = (hex: string) => `${hex}1a`;

const AdminFeedbackDetail: React.FC<AdminFeedbackDetailProps> = ({
  ticketId,
  fallbackTicket,
  isOpen,
  onClose,
  onDeleted,
}) => {
  const { t, i18n } = useTranslation('feedback');
  const locale = i18n.language;
  const queryClient = useQueryClient();

  const enabled = isOpen && ticketId != null;
  const { data: fullTicket } = useGetAdminFeedbackId(ticketId ?? 0, { query: { enabled } });
  const { data: commentsData } = useGetAdminFeedbackIdComments(ticketId ?? 0, { query: { enabled } });

  const ticket = fullTicket ?? fallbackTicket;
  const comments = commentsData ?? [];

  const [composer, setComposer] = useState('');
  const [composerFiles, setComposerFiles] = useState<File[]>([]);
  const [notifyReply, setNotifyReply] = useState(true);
  const [fileError, setFileError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState<'delete' | null>(null);
  const composerInputRef = useRef<HTMLInputElement>(null);

  const [statusDraft, setStatusDraft] = useState<FeedbackStatus | undefined>(undefined);
  const [outcomeDraft, setOutcomeDraft] = useState<ResolutionOutcome | ''>('');
  const [notifyStatus, setNotifyStatus] = useState(true);

  useEffect(() => {
    setComposer('');
    setComposerFiles([]);
    setNotifyReply(true);
    setFileError(null);
    setActionError(null);
    setSending(false);
    setConfirm(null);
    setNotifyStatus(true);
  }, [ticketId]);

  // Keep the status draft in sync with the loaded ticket
  useEffect(() => {
    if (ticket) {
      setStatusDraft(ticket.status);
      setOutcomeDraft(ticket.resolutionOutcome ?? '');
    }
  }, [ticket?.status, ticket?.resolutionOutcome]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: getGetAdminFeedbackQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetAdminFeedbackStatsQueryKey() });
    if (ticketId != null) {
      queryClient.invalidateQueries({ queryKey: getGetAdminFeedbackIdQueryKey(ticketId) });
      queryClient.invalidateQueries({ queryKey: getGetAdminFeedbackIdCommentsQueryKey(ticketId) });
    }
  };

  const commentMutation = usePostAdminFeedbackIdComments({
    mutation: {
      onSuccess: () => {
        invalidateAll();
        setComposer('');
        setComposerFiles([]);
      },
    },
  });
  const statusMutation = usePutAdminFeedbackIdStatus({
    mutation: {
      onSuccess: () => invalidateAll(),
      onError: () => setActionError(t('adminConsole.errors.statusFailed')),
    },
  });
  const deleteMutation = useDeleteAdminFeedbackId({
    mutation: {
      onSuccess: () => {
        invalidateAll();
        setConfirm(null);
        onDeleted();
      },
      onError: () => {
        setConfirm(null);
        setActionError(t('adminConsole.errors.deleteFailed'));
      },
    },
  });

  if (!ticket) {
    return (
      <div className={`${base.overlay} ${isOpen ? base.open : ''}`} onClick={onClose}>
        <aside className={`${base.panel} ${isOpen ? base.open : ''}`} onClick={(e) => e.stopPropagation()} />
      </div>
    );
  }

  const TypeIcon = ticket.type ? TYPE_ICON[ticket.type] : IconDots;
  const statusColor = ticket.status ? STATUS_COLOR[ticket.status] : '#6b7280';
  const priorityColor = ticket.priority ? PRIORITY_COLOR[ticket.priority] : '#6b7280';
  const needsOutcome = !!statusDraft && OUTCOME_STATUSES.includes(statusDraft);
  const statusChanged =
    statusDraft !== ticket.status || (needsOutcome && (outcomeDraft || '') !== (ticket.resolutionOutcome ?? ''));

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    setFileError(null);
    const next = [...composerFiles];
    for (const file of Array.from(list)) {
      if (next.length >= MAX_FILES) {
        setFileError(t('attachments.tooMany', { count: MAX_FILES }));
        break;
      }
      if (file.size > MAX_FILE_BYTES) {
        setFileError(t('attachments.tooLarge', { name: file.name }));
        continue;
      }
      if (!isAllowedType(file.type)) {
        setFileError(t('attachments.unsupported', { name: file.name }));
        continue;
      }
      next.push(file);
    }
    setComposerFiles(next);
    if (composerInputRef.current) composerInputRef.current.value = '';
  };

  const handleDownload = async (att: FeedbackAttachmentDto) => {
    if (att.id == null) return;
    try {
      await downloadAttachment(att.id, att.fileName || 'attachment', true);
    } catch {
      setActionError(t('attachments.downloadFailed'));
    }
  };

  const sendReply = async () => {
    if (!composer.trim() || sending || ticketId == null) return;
    setActionError(null);
    setSending(true);
    try {
      const attachments = composerFiles.length ? await Promise.all(composerFiles.map(fileToPayload)) : undefined;
      await commentMutation.mutateAsync({
        id: ticketId,
        data: { body: composer.trim(), attachments, notify: notifyReply },
      });
    } catch {
      setActionError(t('adminConsole.errors.replyFailed'));
    } finally {
      setSending(false);
    }
  };

  const applyStatus = () => {
    if (ticketId == null || !statusDraft || !statusChanged) return;
    setActionError(null);
    statusMutation.mutate({
      id: ticketId,
      data: {
        status: statusDraft,
        resolutionOutcome: needsOutcome && outcomeDraft ? outcomeDraft : undefined,
        notify: notifyStatus,
      },
    });
  };

  const renderAttachments = (atts?: FeedbackAttachmentDto[]) =>
    atts && atts.length > 0 ? (
      <div className={base.attachmentList}>
        {atts.map((att) => (
          <button key={att.id} type="button" className={base.attachmentChip} onClick={() => handleDownload(att)}>
            {att.contentType?.startsWith('image/') ? <IconPhoto size={14} /> : <IconFile size={14} />}
            <span className={base.attName}>{att.fileName}</span>
            <span className={base.attSize}>{formatFileSize(att.sizeBytes ?? 0)}</span>
            <IconDownload size={14} className={base.attDownload} />
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div className={`${base.overlay} ${isOpen ? base.open : ''}`} onClick={onClose}>
      <aside
        className={`${base.panel} ${isOpen ? base.open : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className={base.header}>
          <div className={base.headTop}>
            <span className={base.ref}>{ticketRef(ticket.id)}</span>
            {ticket.status && (
              <span
                className={base.statusBadge}
                style={{ color: statusColor, backgroundColor: tint(statusColor) }}
              >
                {t(`statuses.${ticket.status}`)}
              </span>
            )}
            {ticket.tenantName && (
              <span className={styles.tenantChip}>
                <IconBuildingStore size={12} />
                {ticket.tenantName}
              </span>
            )}
            <span className={base.spacer} />
            <button
              className={styles.deleteButton}
              type="button"
              onClick={() => setConfirm('delete')}
              aria-label={t('adminConsole.actions.delete')}
              title={t('adminConsole.actions.delete')}
            >
              <IconTrash size={16} />
            </button>
            <button className={base.closeButton} onClick={onClose} type="button" aria-label={t('detail.back')}>
              <IconX size={18} />
            </button>
          </div>
          <h2 className={base.title}>{ticket.title}</h2>
        </header>

        <div className={base.scroll}>
          <div className={base.metaStrip}>
            <span className={base.metaItem}>
              <TypeIcon size={16} />
              {ticket.type ? t(`types.${ticket.type}`) : '—'}
            </span>
            {ticket.priority && (
              <span
                className={base.priorityPill}
                style={{ color: priorityColor, backgroundColor: tint(priorityColor) }}
              >
                {t(`priorities.${ticket.priority}`)}
              </span>
            )}
            {ticket.reporterName && (
              <span className={base.metaItem}>
                <IconUser size={16} />
                {ticket.reporterName}
              </span>
            )}
            {ticket.tenantName && (
              <span className={base.metaItem}>
                <IconBuildingStore size={16} />
                {ticket.tenantName}
              </span>
            )}
            <span className={base.metaItem} title={formatDateTime(ticket.created, locale)}>
              <IconClock size={16} />
              {t('detail.created', { date: formatRelative(ticket.created, locale) })}
            </span>
          </div>

          <section className={base.section}>
            <h3 className={base.sectionTitle}>{t('detail.description')}</h3>
            <p className={base.description}>{ticket.description}</p>
            {renderAttachments(ticket.attachments)}
          </section>

          <div className={styles.statusPanel}>
            <div className={styles.statusPanelTitle}>{t('adminConsole.status.title')}</div>
            <div className={styles.statusControls}>
              <div className={styles.statusField}>
                <label>{t('adminConsole.status.statusLabel')}</label>
                <select
                  value={statusDraft ?? ''}
                  onChange={(e) => setStatusDraft(e.target.value as FeedbackStatus)}
                >
                  {STATUS_VALUES.map((s) => (
                    <option key={s} value={s}>
                      {t(`statuses.${s}`)}
                    </option>
                  ))}
                </select>
              </div>
              {needsOutcome && (
                <div className={styles.statusField}>
                  <label>{t('adminConsole.status.outcomeLabel')}</label>
                  <select
                    value={outcomeDraft}
                    onChange={(e) => setOutcomeDraft(e.target.value as ResolutionOutcome | '')}
                  >
                    <option value="">{t('adminConsole.status.outcomeNone')}</option>
                    {OUTCOME_VALUES.map((o) => (
                      <option key={o} value={o}>
                        {t(`resolutions.${o}`)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button
                className={styles.statusApply}
                type="button"
                onClick={applyStatus}
                disabled={!statusChanged || statusMutation.isPending}
              >
                {statusMutation.isPending ? t('adminConsole.status.applying') : t('adminConsole.status.apply')}
              </button>
            </div>
            <label className={styles.notifyRow}>
              <input type="checkbox" checked={notifyStatus} onChange={(e) => setNotifyStatus(e.target.checked)} />
              {t('adminConsole.status.notify')}
            </label>
          </div>

          <section className={base.section}>
            <h3 className={base.sectionTitle}>{t('detail.thread')}</h3>
            {comments.length === 0 ? (
              <p className={base.noMessages}>{t('adminConsole.detail.noMessages')}</p>
            ) : (
              <div className={base.thread}>
                {comments.map((c) => (
                  <div key={c.id} className={`${base.message} ${c.authorIsAdmin ? base.messageAdmin : ''}`}>
                    <div className={base.messageHead}>
                      <span className={base.messageAuthor}>{c.authorName || '—'}</span>
                      {c.authorIsAdmin && <span className={base.roleBadge}>{t('detail.teamBadge')}</span>}
                      <span className={base.messageTime}>{formatRelative(c.created, locale)}</span>
                    </div>
                    {c.body && <div className={base.messageBody}>{c.body}</div>}
                    {renderAttachments(c.attachments)}
                  </div>
                ))}
              </div>
            )}

            <div className={base.composer}>
              <textarea
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                placeholder={t('adminConsole.detail.composerPlaceholder')}
                rows={3}
              />
              {composerFiles.length > 0 && (
                <ul className={base.composerFiles}>
                  {composerFiles.map((file, index) => (
                    <li key={`${file.name}-${index}`} className={base.composerFile}>
                      {file.type.startsWith('image/') ? <IconPhoto size={14} /> : <IconFile size={14} />}
                      <span className={base.attName}>{file.name}</span>
                      <span className={base.attSize}>{formatFileSize(file.size)}</span>
                      <button
                        type="button"
                        className={base.fileRemove}
                        onClick={() => setComposerFiles((prev) => prev.filter((_, i) => i !== index))}
                      >
                        <IconTrash size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {fileError && <p className={base.error}>{fileError}</p>}
              <label className={`${styles.notifyRow} ${styles.composerNotify}`}>
                <input type="checkbox" checked={notifyReply} onChange={(e) => setNotifyReply(e.target.checked)} />
                {t('adminConsole.detail.notify')}
                <span className={styles.notifyHint}>{t('adminConsole.detail.notifyHint')}</span>
              </label>
              <div className={base.composerBar}>
                <input
                  ref={composerInputRef}
                  type="file"
                  multiple
                  accept={ACCEPT_ATTR}
                  onChange={(e) => addFiles(e.target.files)}
                  hidden
                />
                <button
                  type="button"
                  className={base.composerAttach}
                  onClick={() => composerInputRef.current?.click()}
                >
                  <IconPaperclip size={16} />
                  {t('detail.attach')}
                </button>
                <button
                  type="button"
                  className={base.composerSend}
                  onClick={() => void sendReply()}
                  disabled={!composer.trim() || sending}
                >
                  <IconSend size={15} />
                  {sending ? t('detail.sending') : t('adminConsole.detail.send')}
                </button>
              </div>
            </div>
          </section>

          {actionError && <p className={base.error}>{actionError}</p>}
        </div>
      </aside>

      <ConfirmDialog
        open={confirm === 'delete'}
        title={t('adminConsole.confirm.delete.title')}
        message={t('adminConsole.confirm.delete.body')}
        confirmLabel={t('adminConsole.confirm.delete.confirm')}
        cancelLabel={t('confirm.cancel')}
        loading={deleteMutation.isPending}
        danger
        onCancel={() => setConfirm(null)}
        onConfirm={() => ticketId != null && deleteMutation.mutate({ id: ticketId })}
      />
    </div>
  );
};

export default AdminFeedbackDetail;
